import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { ChevronDown, MessageSquare, ArrowRight, Github, Linkedin } from 'lucide-react';
import { personalInfo } from '../../data/mock';

const TECH_BADGES = ['LangGraph', 'Claude', 'GPT', 'pgvector', 'RAG', 'FastAPI'];

// ─── AI Agent Card Data ───────────────────────────────────────────────────────

const AGENT_SCENES = [
  {
    query: 'How do you prevent hallucination in RAG?',
    thoughts: [
      { text: 'Classifying query intent…', delay: 0 },
      { text: 'Routing → hybrid retrieval', delay: 700 },
      { text: 'Grading chunk relevance', delay: 1400 },
    ],
    tools: [
      { icon: '🔍', label: 'BM25 + pgvector + RRF fusion', status: 'done', ms: '41ms', delay: 2100 },
      { icon: '📊', label: 'Faithfulness · relevance · groundedness', status: 'done', ms: '0.9s', delay: 2700 },
    ],
    answer: 'Score first, ship second. Below threshold, AXIOM rewrites the query and retrieves again: a 13-node loop that argues with itself until the answer holds.',
    answerDelay: 3300,
  },
  {
    query: 'Can your SQL agent drop a table?',
    thoughts: [
      { text: 'Parsing intent → DROP TABLE…', delay: 0 },
      { text: 'Checking Postgres role grants', delay: 700 },
    ],
    tools: [
      { icon: '🔒', label: 'SELECT-only Postgres role', status: 'done', ms: '8ms', delay: 1400 },
      { icon: '🧪', label: 'sqlglot AST validation', status: 'done', ms: '12ms', delay: 2000 },
    ],
    answer: 'No, and not because a prompt says please. NIXUS runs on a SELECT-only Postgres role: refusal enforced by the database itself. 10/10 unsafe requests refused.',
    answerDelay: 2700,
  },
  {
    query: 'Do you trust LLM-generated code?',
    thoughts: [
      { text: 'Spinning up Docker sandbox…', delay: 0 },
      { text: 'Replaying golden I/O suite', delay: 700 },
      { text: 'Scoring structural metrics', delay: 1400 },
    ],
    tools: [
      { icon: '📦', label: 'Container tests vs golden I/O', status: 'done', ms: '2.1s', delay: 2100 },
      { icon: '📐', label: 'Static-metric structural gate', status: 'done', ms: '340ms', delay: 2700 },
    ],
    answer: 'Never. Darwin makes it prove itself: three objective gates, no model in the scoring path. ~95% pass on the first attempt, never worse than baseline.',
    answerDelay: 3300,
  },
  {
    query: 'Production RAG is slow. Fix it.',
    thoughts: [
      { text: 'Embedding query…', delay: 0 },
      { text: 'Semantic cache lookup → HIT', delay: 700 },
    ],
    tools: [
      { icon: '⚡', label: 'Two-tier Redis semantic cache', status: 'done', ms: '3ms', delay: 1400 },
      { icon: '📡', label: 'Prod p95 monitor · <900ms', status: 'done', ms: '22ms', delay: 2000 },
    ],
    answer: 'By answering before the LLM even wakes up. Two-tier Redis semantic cache: 30–50× faster on hits, p95 under 900ms in production, 28% lower infra cost.',
    answerDelay: 2700,
  },
  {
    query: 'Tell me about your multi-agent system.',
    thoughts: [
      { text: 'Expanding topic → agent topology…', delay: 0 },
      { text: 'Compiling supervisor graph', delay: 700 },
    ],
    tools: [
      { icon: '🕸️', label: 'Send() → parallel fact-check ×6', status: 'done', ms: '2.8s', delay: 1400 },
      { icon: '✋', label: 'interrupt() · outline approval', status: 'wait', delay: 2000 },
    ],
    answer: 'ResearchForge: 6 agents, one deterministic supervisor, zero LLM routing. A human approves the outline mid-graph; regeneration skips unchanged sections, ~80% fewer tokens.',
    answerDelay: 2700,
  },
  {
    query: 'Prompting is easy. Can you fine-tune?',
    thoughts: [
      { text: 'Loading QLoRA adapters…', delay: 0 },
      { text: 'Resolving 4-bit NF4 config', delay: 700 },
    ],
    tools: [
      { icon: '⚙️', label: 'Unsloth · LoRA rank 128 · NF4', status: 'done', ms: '1.4s', delay: 1400 },
      { icon: '📈', label: 'W&B run v4 logged', status: 'done', ms: '96ms', delay: 2000 },
    ],
    answer: 'LLaMA 3.1 8B on 14,400 pharma regulatory samples: QLoRA rank 128, 4-bit NF4 via Unsloth. Four versions tracked in W&B, shipped with CFR citation guardrails.',
    answerDelay: 2700,
  },
  {
    query: 'What happens when a prompt is vague?',
    thoughts: [
      { text: 'Running pre-flight clarity check…', delay: 0 },
      { text: 'Verdict: underspecified', delay: 700 },
    ],
    tools: [
      { icon: '🛡️', label: 'inputguard · 5 classifiers · 8 rule sets', status: 'done', ms: '4ms', delay: 1400 },
      { icon: '🔏', label: 'ai-stamp · HMAC-signed record', status: 'done', ms: '11ms', delay: 2000 },
    ],
    answer: 'Caught before a single token is spent. inputguard scores clarity locally in milliseconds; ai-stamp signs every output with an HMAC audit trail. Both live on PyPI.',
    answerDelay: 2700,
  },
  {
    query: 'How do you catch regressions before users do?',
    thoughts: [
      { text: 'Pulling LangSmith traces…', delay: 0 },
      { text: 'Diffing hallucination rate vs baseline', delay: 700 },
    ],
    tools: [
      { icon: '📡', label: 'LangSmith full-pipeline trace', status: 'done', ms: '130ms', delay: 1400 },
      { icon: '🧪', label: 'OpenAI Evals pre-release gate', status: 'done', ms: '1.8s', delay: 2000 },
    ],
    answer: 'Trace everything, score continuously. LangSmith + OpenAI Evals turned regression detection from a post-deploy fire drill into a pre-release checkpoint.',
    answerDelay: 2700,
  },
  {
    query: 'Are you available for hire?',
    thoughts: [
      { text: 'Parsing recruiter intent…', delay: 0 },
      { text: 'Compiling proof of work', delay: 700 },
    ],
    tools: [
      { icon: '🚀', label: '12+ production AI systems', status: 'done', delay: 1400 },
      { icon: '📣', label: '3× Top AI Voice · 10M+ impressions', status: 'done', delay: 2000 },
    ],
    answer: 'Yes, open to AI engineer roles right now. Everything this card just showed you is shipped, verifiable work. Scroll down, or ask my chatbot the hard questions.',
    answerDelay: 2700,
  },
];

// ─── Name animation variants ─────────────────────────────────────────────────

const nameContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.036, delayChildren: 0.65 } },
};
const nameChar = {
  hidden:  { y: 70, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 260, damping: 22 } },
};

// ─── Streaming token hook ─────────────────────────────────────────────────────

const useStream = (text, startMs, speed = 22) => {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    setDisplayed('');
    if (!text) return;
    let id;
    const t0 = setTimeout(() => {
      let i = 0;
      id = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) clearInterval(id);
      }, speed);
    }, startMs);
    return () => {
      clearTimeout(t0);
      if (id) clearInterval(id);
    };
  }, [text, startMs, speed]);
  return displayed;
};

// ─── AI Agent Card ────────────────────────────────────────────────────────────

const AgentCard = () => {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [phase, setPhase]       = useState('query');   // query | thinking | tools | answer | pause
  const [visThoughts, setVisThoughts] = useState([]);
  const [visTools, setVisTools]       = useState([]);
  const [blink, setBlink]             = useState(true);
  const introDone = useRef(false);

  const scene = AGENT_SCENES[sceneIdx];

  // Blink cursor
  useEffect(() => {
    const id = setInterval(() => setBlink(b => !b), 520);
    return () => clearInterval(id);
  }, []);

  // Scene orchestration
  useEffect(() => {
    setPhase('query');
    setVisThoughts([]);
    setVisTools([]);
    const timers = [];

    // Hold the first scene until the card's entrance animation has settled,
    // so the card doesn't reflow (and judder) while it is still flying in
    const offset = introDone.current ? 0 : 1200;

    // Show thinking tokens
    timers.push(setTimeout(() => {
      introDone.current = true;
      setPhase('thinking');
    }, offset + 900));
    scene.thoughts.forEach(th => {
      timers.push(setTimeout(() =>
        setVisThoughts(p => [...p, th.text]), offset + 900 + th.delay
      ));
    });

    // Show tool calls
    timers.push(setTimeout(() => setPhase('tools'),
      offset + 900 + (scene.thoughts.at(-1)?.delay ?? 0) + 500
    ));
    scene.tools.forEach(tool => {
      timers.push(setTimeout(() =>
        setVisTools(p => [...p, tool]), offset + 900 + tool.delay
      ));
    });

    // Show answer
    timers.push(setTimeout(() => setPhase('answer'), offset + scene.answerDelay));

    // Pause then next scene
    const sceneDuration = offset + scene.answerDelay + scene.answer.length * 22 + 2800;
    timers.push(setTimeout(() => {
      setSceneIdx(i => (i + 1) % AGENT_SCENES.length);
    }, sceneDuration));

    return () => timers.forEach(clearTimeout);
  }, [sceneIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const answerText = useStream(
    phase === 'answer' ? scene.answer : '',
    0,
    20
  );

  // 3-D tilt
  const containerRef = useRef(null);
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  const rotateX = useTransform(mouseY, [0, 1], [4, -4]);
  const rotateY = useTransform(mouseX, [0, 1], [-5, 5]);
  const springX = useSpring(rotateX, { stiffness: 110, damping: 22 });
  const springY = useSpring(rotateY, { stiffness: 110, damping: 22 });
  const onMouseMove = (e) => {
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - r.left) / r.width);
    mouseY.set((e.clientY - r.top) / r.height);
  };
  const onMouseLeave = () => { mouseX.set(0.5); mouseY.set(0.5); };

  return (
    <div style={{ perspective: '900px' }}>
      <motion.div
        ref={containerRef}
        style={{ rotateX: springX, rotateY: springY }}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className="relative cursor-default"
      >
        {/* Ambient glow */}
        <div
          className="absolute -inset-8 rounded-2xl pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(0,212,255,0.16) 0%, rgba(130,60,255,0.06) 50%, transparent 75%)',
            filter: 'blur(24px)',
          }}
        />

        {/* Card */}
        <div
          className="glass-card rounded-2xl overflow-hidden relative"
          style={{
            border: '1px solid rgba(0,212,255,0.16)',
            boxShadow: '0 12px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              background: 'linear-gradient(90deg, rgba(0,212,255,0.07) 0%, rgba(130,60,255,0.05) 100%)',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-[#00D4FF] animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(0,212,255,0.3)' }} />
                <span className="w-1 h-1 rounded-full" style={{ background: 'rgba(0,212,255,0.15)' }} />
              </div>
              <span className="font-mono text-[11px] font-semibold" style={{ color: '#00D4FF' }}>AI AGENT</span>
              <span className="font-mono text-[9px] text-[#3A4E5E] ml-1">nihanth-rag-v2</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
              <span className="font-mono text-[9px] tracking-widest" style={{ color: '#39FF14' }}>LIVE</span>
            </div>
          </div>

          <div className="p-4 space-y-3" style={{ minHeight: '260px' }}>

            {/* User query bubble */}
            <motion.div
              key={`q-${sceneIdx}`}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35 }}
              className="flex justify-end"
            >
              <div
                className="px-3 py-2 rounded-xl rounded-tr-sm font-mono text-[11px] leading-relaxed max-w-[85%]"
                style={{
                  background: 'rgba(0,212,255,0.12)',
                  border: '1px solid rgba(0,212,255,0.22)',
                  color: '#C0E8F8',
                }}
              >
                {scene.query}
              </div>
            </motion.div>

            {/* Thinking section */}
            {phase !== 'query' && (
              <div
                className="rounded-xl p-3 space-y-1.5"
                style={{
                  background: 'rgba(130,60,255,0.06)',
                  border: '1px solid rgba(130,60,255,0.14)',
                }}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-[10px]">🧠</span>
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: '#9B6AFF' }}>THINKING</span>
                </div>
                {visThoughts.map((th, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2"
                  >
                    <span className="text-[#9B6AFF] text-[10px]">→</span>
                    <span className="font-mono text-[10px] text-[#7A6A9A]">{th}</span>
                  </motion.div>
                ))}
                {phase === 'thinking' && (
                  <span
                    className="inline-block w-[1.5px] h-[10px] align-middle ml-1"
                    style={{ background: '#9B6AFF', opacity: blink ? 0.9 : 0 }}
                  />
                )}
              </div>
            )}

            {/* Tool calls */}
            {visTools.length > 0 && (
              <div className="space-y-1.5">
                {visTools.map((tool, i) => {
                  const waiting = tool.status === 'wait';
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                      style={{
                        background: waiting ? 'rgba(245,166,35,0.05)' : 'rgba(57,255,20,0.04)',
                        border: waiting ? '1px solid rgba(245,166,35,0.16)' : '1px solid rgba(57,255,20,0.12)',
                      }}
                    >
                      <span className="text-[12px]">{tool.icon}</span>
                      <span className="font-mono text-[10px] flex-1" style={{ color: waiting ? '#8A7248' : '#4A6A4A' }}>
                        {tool.label}
                      </span>
                      <span className="font-mono text-[9px]" style={{ color: waiting ? '#F5A623' : '#39FF14' }}>
                        {waiting ? '⏸ waiting' : `✓ ${tool.ms || 'done'}`}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Agent answer bubble */}
            {phase === 'answer' && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35 }}
                className="flex justify-start"
              >
                <div
                  className="px-3 py-2 rounded-xl rounded-tl-sm font-mono text-[11px] leading-relaxed max-w-[92%]"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#A8B8C8',
                  }}
                >
                  {answerText}
                  {answerText.length < scene.answer.length && (
                    <span
                      className="inline-block w-[1.5px] h-[11px] align-middle ml-px"
                      style={{ background: '#00D4FF', opacity: blink ? 1 : 0 }}
                    />
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer model tag */}
          <div
            className="flex items-center justify-between px-4 py-2"
            style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: 'rgba(0,0,0,0.15)' }}
          >
            <span className="font-mono text-[9px] text-[#2A3A4A]">Claude · LangGraph · pgvector</span>
            <div className="flex gap-1">
              {AGENT_SCENES.map((_, i) => (
                <span
                  key={i}
                  className="w-1 h-1 rounded-full transition-all duration-300"
                  style={{ background: i === sceneIdx ? '#00D4FF' : 'rgba(255,255,255,0.1)' }}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Main HeroSection ─────────────────────────────────────────────────────────

const HeroSection = () => {
  const [roleIndex,  setRoleIndex]  = useState(0);
  const [text,       setText]       = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentRole = personalInfo.roles[roleIndex];
    let timeout;
    if (!isDeleting) {
      if (text.length < currentRole.length) {
        timeout = setTimeout(() => setText(currentRole.slice(0, text.length + 1)), 80);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 2200);
      }
    } else {
      if (text.length > 0) {
        timeout = setTimeout(() => setText(text.slice(0, -1)), 40);
      } else {
        setIsDeleting(false);
        setRoleIndex(prev => (prev + 1) % personalInfo.roles.length);
      }
    }
    return () => clearTimeout(timeout);
  }, [text, isDeleting, roleIndex]);

  const nameChars = 'NIHANTH NAIDU K'.split('');

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">

      {/* Subtle directional glow beams */}
      <div className="absolute inset-0 pointer-events-none z-[1]">
        <div
          className="absolute top-0 left-[15%] w-[40vw] h-[70vh] opacity-[0.06]"
          style={{
            background: 'radial-gradient(ellipse at top, #00D4FF 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <div
          className="absolute bottom-0 right-[10%] w-[35vw] h-[50vh] opacity-[0.04]"
          style={{
            background: 'radial-gradient(ellipse at bottom, #9B59B6 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-16 grid lg:grid-cols-[1fr_420px] gap-14 xl:gap-20 items-center relative z-10 w-full">

        {/* ── LEFT ── */}
        <div>
          {/* Status badges row */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="flex flex-wrap items-center gap-3 mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card">
              <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" />
              <span className="font-mono text-sm text-[#8892A4]">{personalInfo.currentBadge}</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.2)' }}
            >
              <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" />
              <span className="font-mono text-xs font-semibold" style={{ color: '#39FF14' }}>Available for new roles</span>
            </div>
          </motion.div>

          {/* Animated name: each character slides up */}
          <div className="overflow-hidden mb-5">
            <motion.h1
              className="font-display font-extrabold leading-[0.9] tracking-tight"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', whiteSpace: 'nowrap' }}
              variants={nameContainer}
              initial="hidden"
              animate="visible"
            >
              {nameChars.map((char, i) => (
                <motion.span
                  key={i}
                  variants={nameChar}
                  className="inline-block"
                  style={{
                    backgroundImage: 'linear-gradient(150deg, #FFFFFF 0%, #D0E4FF 40%, #9FC4E8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>
              ))}
            </motion.h1>
          </div>

          {/* Typewriter role */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="h-11 mb-6 flex items-center"
          >
            <span className="font-mono text-[#00D4FF] font-semibold tracking-wide"
              style={{ fontSize: 'clamp(0.95rem, 1.8vw, 1.35rem)' }}>
              {text}
              <span className="animate-blink text-[#00D4FF]">|</span>
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7 }}
            className="text-[#8892A4] mb-10 max-w-[480px] leading-relaxed"
            style={{ fontSize: 'clamp(0.875rem, 1.2vw, 1rem)' }}
          >
            {personalInfo.subtitle}
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.9 }}
            className="flex gap-4 flex-wrap mb-10"
          >
            <a
              href="#projects"
              className="hero-cta-primary group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg font-mono text-sm text-[#00D4FF] transition-all duration-300 hover:scale-[1.03]"
            >
              View My Work
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
            </a>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openChat'))}
              className="hero-cta-secondary group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg font-mono text-sm text-[#C0C8D8] transition-all duration-300"
            >
              <MessageSquare
                size={15}
                className="text-[#8892A4] group-hover:text-[#00D4FF] transition-colors duration-200"
              />
              Chat With My AI
            </button>
          </motion.div>

          {/* Visitor social proof */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.05 }}
            className="font-mono text-xs text-[#3A4A5A] mb-5"
          >
            👀 Visited by 500+ recruiters and engineers
          </motion.p>

          {/* Tech badge row + socials */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.1 }}
            className="flex flex-wrap gap-2 items-center"
          >
            {TECH_BADGES.map((badge, i) => (
              <span
                key={i}
                className="px-3 py-1 rounded-full font-mono text-xs border border-[rgba(255,255,255,0.07)] text-[#4A5668] hover:border-[rgba(0,212,255,0.22)] hover:text-[#7A8898] transition-all duration-200 cursor-default"
              >
                {badge}
              </span>
            ))}
            <span className="flex items-center gap-2">
              <span className="w-px h-4 bg-[rgba(255,255,255,0.08)] mx-1" />
              <a
                href={personalInfo.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-xs border border-[rgba(255,255,255,0.07)] text-[#4A5668] hover:border-[rgba(0,212,255,0.28)] hover:text-[#00D4FF] transition-all duration-200"
              >
                <Github size={11} /> GitHub
              </a>
              <a
                href={personalInfo.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-xs border border-[rgba(255,255,255,0.07)] text-[#4A5668] hover:border-[rgba(0,212,255,0.28)] hover:text-[#00D4FF] transition-all duration-200"
              >
                <Linkedin size={11} /> LinkedIn
              </a>
            </span>
          </motion.div>
        </div>

        {/* ── RIGHT: Terminal card ── */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block"
        >
          {/* Label row above card */}
          <div className="flex items-center mb-3 px-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] animate-pulse mr-1.5" />
            <span className="font-mono text-[10px] tracking-widest text-[#3A4E5E]">AI AGENT</span>
          </div>
          <AgentCard />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className="font-mono text-[10px] tracking-[0.25em] text-[#8892A4] opacity-50">SCROLL</span>
        <ChevronDown size={18} className="text-[#00D4FF] opacity-50" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
